import { useMemo } from "react";
import classNames from "classnames";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import type { CollapsibleViewButtonProps } from "@/features/collapsible-view";
import { CollapsibleViewButton } from "@/features/collapsible-view";
import type { FeatureContainerLayout, FormLevelFeature } from "@/entities/form-metadata";
import { FormLevelFeatureType, useMetadataQuery } from "@/entities/form-metadata";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { Grid } from "@/shared/components/Grid";
import { useErrorBoundaryLogger } from "@/shared/lib/fci";
import type { AdvancedSearchLinkProps } from "./AdvancedSearchLink/AdvancedSearchLink";
import { AdvancedSearchLink } from "./AdvancedSearchLink/AdvancedSearchLink";
import type { CategoryBucketProps } from "./CategoryBucket/CategoryBucket";
import { CategoryBucket } from "./CategoryBucket/CategoryBucket";
import type { ClearSectionProps } from "./Clear/ClearSection";
import { ClearSection } from "./Clear/ClearSection";
import type { CollectionFocusProps } from "./CollectionFocus/CollectionFocus";
import { CollectionFocus } from "./CollectionFocus/CollectionFocus";
import type { LimitToUserCountryProps } from "./LimitToUserCountry/LimitToUserCountry";
import { LimitToUserCountry } from "./LimitToUserCountry/LimitToUserCountry";
import { MatchAllTerms } from "./MatchAllTerms/MatchAllTerms";
import type { MessageProps } from "./Message/Message";
import { Message } from "./Message/Message";
import { RotatedRecords } from "./RotatedRecords";
import { SearchAssistModal, type SearchAssistModalProps } from "./SearchAssistModal";
import type { SubmitSectionProps } from "./Submit/SubmitSection";
import { SubmitSection } from "./Submit/SubmitSection";
import styles from "./FeatureContainerRenderer.module.scss";

interface FeatureComponentMapperProps {
    feature: FormLevelFeature;
}
const FeatureComponentMapper = ({ feature }: FeatureComponentMapperProps) => {
    const logger = useErrorBoundaryLogger();

    switch (feature.featureType) {
        // Essential features — errors propagate to RemoteErrorBoundary
        case FormLevelFeatureType.CollapsibleView:
            return <CollapsibleViewButton {...(feature.attributes as CollapsibleViewButtonProps)} />;
        case FormLevelFeatureType.SubmitBtn:
            return <SubmitSection {...(feature.attributes as SubmitSectionProps)} />;
        case FormLevelFeatureType.CategoryBuckets:
            return <CategoryBucket {...(feature.attributes as CategoryBucketProps)} />;

        // Non-essential features — wrapped in ErrorBoundary to prevent
        // a crash in one feature from taking down the entire form
        case FormLevelFeatureType.Message:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <Message {...(feature.attributes as MessageProps)} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.ClearBtn:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <ClearSection {...(feature.attributes as ClearSectionProps)} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.AdvancedSearchLink:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <AdvancedSearchLink {...(feature.attributes as AdvancedSearchLinkProps)} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.LimitToCountry:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <LimitToUserCountry {...(feature.attributes as LimitToUserCountryProps)} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.CollectionFocus:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <CollectionFocus {...(feature.attributes as CollectionFocusProps)} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.SearchAssistModal:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <SearchAssistModal {...(feature.attributes as SearchAssistModalProps)} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.RotatedRecords:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <RotatedRecords />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.MatchAllTerms:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <MatchAllTerms />
                </ErrorBoundary>
            );
        default:
            return null;
    }
};

interface FeatureContainerRendererProps {
    uuids: string[];
    layout?: FeatureContainerLayout;
}
export const FeatureContainerRenderer = ({ uuids, layout }: FeatureContainerRendererProps) => {
    const { data: formMetadata } = useMetadataQuery();

    const features = useMemo(() => {
        const featuresMap = new Map(formMetadata.features.map((f) => [f.UUID, f]));
        return uuids.map((uuid) => featuresMap.get(uuid)!);
    }, [uuids, formMetadata.features]);

    const hasSearchAssistModal = useMemo(
        () => features.some((f) => f.featureType === FormLevelFeatureType.SearchAssistModal),
        [features],
    );

    const hasNonVisualFeature = useMemo(
        () =>
            features.some(
                (f) =>
                    f.featureType === FormLevelFeatureType.CollapsibleStart ||
                    f.featureType === FormLevelFeatureType.CollapsibleEnd,
            ),
        [features],
    );

    return (
        <div
            className={classNames(styles.featureContainerRenderer, {
                [styles.noMargin as string]: hasSearchAssistModal || hasNonVisualFeature,
            })}
            data-test-sfs-container-type="featureContainer"
        >
            <Grid.Container>
                <Grid.Field spanDesktop={12} spanMobile={12} spanTablet={12}>
                    <div className={classNames({ [styles.horizontal as string]: layout === "horizontal" })}>
                        {features.map((feature) => (
                            <div key={feature.UUID} data-test-sfs-feature-type={feature.featureType}>
                                <FeatureComponentMapper feature={feature} />
                            </div>
                        ))}
                    </div>
                </Grid.Field>
            </Grid.Container>
        </div>
    );
};
